import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  AppBar,
  Toolbar,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  CircularProgress,
  LinearProgress,
} from "@mui/material";
import Atras from "./atras";
import BottomNav from "./BottomNav";
import { auth, db } from "../firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { SKILL_QUIZZES } from "./skillQuizzes";
import { CertificationChip, getCertificationLevel } from "./certifications";
import UnlockCertificationDialog from "./UnlockCertificationDialog";

const SkillTest = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userSkills, setUserSkills] = useState([]);
  const [assessments, setAssessments] = useState({});
  const [activeSkill, setActiveSkill] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);

  const loadUser = async () => {
    const user = auth.currentUser;
    if (!user) {
      navigate("/login");
      return;
    }
    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (userDoc.exists()) {
      const data = userDoc.data();
      setUserSkills(Array.isArray(data.skills) ? data.skills : []);
      setAssessments(data.skillAssessments || {});
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTest = (skill) => {
    setActiveSkill(skill);
    setAnswers(new Array(SKILL_QUIZZES[skill]?.length || 0).fill(null));
    setResult(null);
  };

  const handleAnswer = (index, optionIndex) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[index] = optionIndex;
      return next;
    });
  };

  const allAnswered = answers.length > 0 && answers.every((a) => a !== null);

  const handleSubmit = async () => {
    const questions = SKILL_QUIZZES[activeSkill];
    const correct = questions.reduce(
      (acc, q, i) => acc + (answers[i] === q.correctIndex ? 1 : 0),
      0
    );
    const score = Math.round((correct / questions.length) * 100);
    const passed = score >= 60;
    const existingDisplayed = assessments[activeSkill]?.displayed || false;

    const user = auth.currentUser;
    await updateDoc(doc(db, "users", user.uid), {
      [`skillAssessments.${activeSkill}`]: {
        score,
        completedAt: serverTimestamp(),
        displayed: passed ? existingDisplayed : false,
      },
    });

    setAssessments((prev) => ({
      ...prev,
      [activeSkill]: { score, displayed: passed ? existingDisplayed : false },
    }));
    setResult({ score, passed });
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container>
      <AppBar position="fixed">
        <Toolbar sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Atras />
          <Typography variant="h6" color="text.primary">
            Examen de habilidades
          </Typography>
        </Toolbar>
      </AppBar>
      <Container sx={{ mt: 15, mb: 14 }}>
        {!activeSkill && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Rendí un examen corto por cada habilidad de tu perfil. Si aprobás (60% o más),
              podés pagar para mostrar la certificación en tu perfil público.
            </Typography>
            {userSkills.length === 0 && (
              <Typography color="text.secondary">
                Todavía no cargaste habilidades en tu perfil. Agregalas desde "Editar Perfil" para poder rendir un examen.
              </Typography>
            )}
            {userSkills.map((skill) => {
              const assessment = assessments[skill];
              const level = assessment ? getCertificationLevel(assessment.score) : null;
              const hasQuiz = !!SKILL_QUIZZES[skill];
              return (
                <Card key={skill} sx={{ p: 2, mb: 2 }}>
                  <CardContent sx={{ p: "0 !important" }}>
                    <Typography variant="h6">{skill}</Typography>
                    {assessment ? (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Último puntaje: {assessment.score}%
                        </Typography>
                        {level ? (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            <CertificationChip skill={skill} score={assessment.score} />
                            {assessment.displayed ? (
                              <Typography variant="caption" color="success.main">
                                ✓ Visible en tu perfil
                              </Typography>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setActiveSkill(skill);
                                  setResult({ score: assessment.score, passed: true });
                                }}
                              >
                                Mostrar en mi perfil
                              </Button>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="error">
                            No alcanzaste el puntaje mínimo (60%)
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Todavía no rendiste este examen
                      </Typography>
                    )}
                    <Button
                      sx={{ mt: 2 }}
                      variant="contained"
                      disabled={!hasQuiz}
                      onClick={() => startTest(skill)}
                    >
                      {assessment ? "Reintentar examen" : "Tomar examen"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </>
        )}

        {activeSkill && !result && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Examen: {activeSkill}
            </Typography>
            {SKILL_QUIZZES[activeSkill].map((q, i) => (
              <FormControl key={i} component="fieldset" sx={{ mb: 3, width: "100%" }}>
                <FormLabel component="legend">
                  {i + 1}. {q.question}
                </FormLabel>
                <RadioGroup
                  value={answers[i] === null || answers[i] === undefined ? "" : String(answers[i])}
                  onChange={(e) => handleAnswer(i, Number(e.target.value))}
                >
                  {q.options.map((option, optIndex) => (
                    <FormControlLabel
                      key={optIndex}
                      value={String(optIndex)}
                      control={<Radio />}
                      label={option}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            ))}
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setActiveSkill(null)}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                disabled={!allAnswered}
                onClick={handleSubmit}
              >
                Enviar examen
              </Button>
            </Box>
          </Box>
        )}

        {activeSkill && result && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h5" gutterBottom>
              Puntaje: {result.score}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={result.score}
              sx={{ mb: 3, height: 8, borderRadius: 4 }}
            />
            {result.passed ? (
              <>
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <CertificationChip skill={activeSkill} score={result.score} size="medium" />
                </Box>
                {assessments[activeSkill]?.displayed ? (
                  <Typography color="success.main" sx={{ mb: 2 }}>
                    Esta certificación ya está visible en tu perfil.
                  </Typography>
                ) : (
                  <Button
                    variant="contained"
                    sx={{ mb: 2 }}
                    onClick={() => setUnlockDialogOpen(true)}
                  >
                    Mostrar en mi perfil
                  </Button>
                )}
              </>
            ) : (
              <Typography color="error" sx={{ mb: 2 }}>
                No alcanzaste el puntaje mínimo (60%) para certificarte. ¡Podés volver a intentarlo!
              </Typography>
            )}
            <Box>
              <Button
                variant="outlined"
                onClick={() => {
                  setActiveSkill(null);
                  setResult(null);
                }}
              >
                Volver
              </Button>
            </Box>
          </Box>
        )}
      </Container>
      <UnlockCertificationDialog
        open={unlockDialogOpen}
        skill={activeSkill}
        score={result?.score}
        onClose={() => setUnlockDialogOpen(false)}
        onUnlocked={() =>
          setAssessments((prev) => ({
            ...prev,
            [activeSkill]: { ...prev[activeSkill], displayed: true },
          }))
        }
      />
      <BottomNav />
    </Container>
  );
};

export default SkillTest;
